import backendV2Service from './backend-v2.service';

/**
 * School Arrangement interface matching backend response
 */
export interface SchoolArrangement {
  id: number;
  name?: string;
  emailCustomerCs?: string;
  totalLicenses: number;
  status: string;
  arrangementType: string;
  startDate: string; // ISO date string or YYYY-MM-DD
  endDate: string; // ISO date string or YYYY-MM-DD
  schoolId?: number;
  schoolGroupId?: number;
  createdDate: string;
  updatedDate?: string;
  renewedTracking?: number;
}

/**
 * DTO for creating/updating school arrangements
 */
export interface SchoolArrangementDto {
  name?: string;
  emailCustomerCs?: string;
  totalLicenses: number;
  arrangementType: string;
  startDate: string;
  endDate: string;
  schoolId?: number;
  schoolGroupId?: number;
  renewedTracking?: number;
}

/**
 * Legacy response format from main-backend (maintained for compatibility)
 */
export interface SchoolArrangementResponse {
  status: string;
  content: SchoolArrangement | null;
}

export interface SchoolArrangementListResponse {
  status: string;
  content: SchoolArrangement[];
}

const unwrap = <T>(request: Promise<{ data: T }>) => request.then(({ data }) => data);

export default {
  /**
   * Find school arrangement by school ID
   * GET /school-arrangements?school_id={schoolId}
   */
  findBySchoolId: (
    schoolId: number | string,
  ): Promise<SchoolArrangementResponse> => unwrap(
    backendV2Service.get<SchoolArrangementResponse>(`/school-arrangements?school_id=${schoolId}`),
  ),

  /**
   * Find school arrangement by school group ID
   * GET /school-arrangements?school_group_id={schoolGroupId}
   */
  findBySchoolGroupId: (
    schoolGroupId: number | string,
  ): Promise<SchoolArrangementResponse> => unwrap(
    backendV2Service.get<SchoolArrangementResponse>(`/school-arrangements?school_group_id=${schoolGroupId}`),
  ),

  /**
   * Find school arrangement by ID
   * GET /school-arrangements/{id}
   */
  findById: (id: number | string): Promise<SchoolArrangementResponse> => unwrap(
    backendV2Service.get<SchoolArrangementResponse>(`/school-arrangements/${id}`),
  ),

  /**
   * Create a new school arrangement
   * POST /school-arrangements
   */
  create: (data: SchoolArrangementDto): Promise<SchoolArrangementResponse> => unwrap(
    backendV2Service.post<SchoolArrangementResponse>('/school-arrangements', data),
  ),

  /**
   * Update an existing school arrangement
   * PUT /school-arrangements/{id}
   */
  update: (
    id: number | string,
    data: Partial<SchoolArrangementDto>,
  ): Promise<SchoolArrangementResponse> => unwrap(
    backendV2Service.put<SchoolArrangementResponse>(`/school-arrangements/${id}`, data),
  ),

  /**
   * Upsert (create or update) school arrangement
   * POST /school-arrangements/upsert
   */
  upsert: (data: SchoolArrangementDto): Promise<SchoolArrangementResponse> => unwrap(
    backendV2Service.post<SchoolArrangementResponse>('/school-arrangements/upsert', data),
  ),

  /**
   * List all arrangements for a school group (history)
   * GET /school-arrangements/by-school-group/{schoolGroupId}
   */
  findAllBySchoolGroupId: (
    schoolGroupId: number | string,
  ): Promise<SchoolArrangementListResponse> => unwrap(
    backendV2Service.get<SchoolArrangementListResponse>(
      `/school-arrangements/by-school-group/${schoolGroupId}`,
    ),
  ),

  /**
   * List all arrangements for a school (history)
   * GET /school-arrangements/by-school/{schoolId}
   */
  findAllBySchoolId: (
    schoolId: number | string,
  ): Promise<SchoolArrangementListResponse> => unwrap(
    backendV2Service.get<SchoolArrangementListResponse>(
      `/school-arrangements/by-school/${schoolId}`,
    ),
  ),
};
